/**
 * MeetingFormModal - Модальное окно для создания/редактирования встречи
 */
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Textarea,
  MultiSelect,
  Button,
  Group,
  Stack,
  Alert,
  Divider,
} from '@mantine/core';
import { DatePickerInput, TimeInput } from '@mantine/dates';
import { IconAlertCircle, IconCalendarEvent, IconCalendar, IconClock } from '@tabler/icons-react';
import { useSession } from '../../../context/SessionContext';

/**
 * Форматирование полного имени контакта
 */
const formatContactName = (contact) => {
  const parts = [];
  if (contact.last_name) parts.push(contact.last_name);
  if (contact.first_name) parts.push(contact.first_name);
  if (contact.middle_name) parts.push(contact.middle_name);
  const name = parts.join(' ') || 'Без имени';
  return contact.position ? `${name} (${contact.position})` : name;
};

/**
 * Форматирование имени пользователя
 */
const formatUserName = (user) => {
  if (user.full_name) return user.full_name;
  const parts = [];
  if (user.last_name) parts.push(user.last_name);
  if (user.first_name) parts.push(user.first_name);
  return parts.join(' ') || user.email;
};

const MeetingFormModal = ({
  opened,
  onClose,
  onSubmit,
  meeting = null, // null = создание, object = редактирование
  organizationId,
  contacts = [], // Список контактов организации
  loading = false,
}) => {
  const { user: currentUser } = useSession();
  const isEditing = !!meeting;

  const [formData, setFormData] = useState({
    contact_ids: [],
    participant_user_ids: [],
    comment: '',
    meeting_date: new Date(),
    meeting_time: '',
  });
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Загрузка списка пользователей (все роли могут участвовать во встречах)
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoadingUsers(true);
        const response = await fetch('/api/users/all-active', {
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          setUsers(data.items || []);
        }
      } catch (err) {
        console.error('Failed to load users:', err);
      } finally {
        setLoadingUsers(false);
      }
    };

    if (opened) {
      fetchUsers();
    }
  }, [opened]);

  // Форматирование времени из даты
  const formatTimeFromDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Инициализация формы при открытии
  useEffect(() => {
    if (opened) {
      if (meeting) {
        // Редактирование - заполняем данными
        const meetingDate = new Date(meeting.meeting_datetime);
        setFormData({
          contact_ids: meeting.contacts?.map(c => c.id.toString()) || [],
          participant_user_ids: meeting.participants?.map(p => p.id.toString()) || [],
          comment: meeting.comment || '',
          meeting_date: meetingDate,
          meeting_time: formatTimeFromDate(meetingDate),
        });
      } else {
        // Создание - текущая дата/время и текущий пользователь
        const now = new Date();
        setFormData({
          contact_ids: [],
          participant_user_ids: currentUser?.id ? [currentUser.id.toString()] : [],
          comment: '',
          meeting_date: now,
          meeting_time: formatTimeFromDate(now),
        });
      }
      setError(null);
    }
  }, [opened, meeting, currentUser]);

  // Подготовка данных для MultiSelect контактов
  const contactOptions = contacts.map((contact) => ({
    value: contact.id.toString(),
    label: formatContactName(contact),
  }));

  // Подготовка данных для MultiSelect пользователей
  const userOptions = users.map((user) => ({
    value: user.id.toString(),
    label: formatUserName(user),
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Валидация
    if (!formData.contact_ids || formData.contact_ids.length === 0) {
      setError('Выберите хотя бы одного контакта');
      return;
    }

    if (!formData.participant_user_ids || formData.participant_user_ids.length === 0) {
      setError('Выберите хотя бы одного участника встречи');
      return;
    }

    if (!formData.meeting_date) {
      setError('Укажите дату встречи');
      return;
    }

    if (!formData.meeting_time) {
      setError('Укажите время встречи');
      return;
    }

    try {
      // Собираем дату и время в один datetime
      const [hours, minutes] = formData.meeting_time.split(':').map(Number);
      const meetingDatetime = new Date(formData.meeting_date);
      meetingDatetime.setHours(hours || 0, minutes || 0, 0, 0);

      const data = {
        organization_id: organizationId,
        contact_ids: formData.contact_ids.map(id => parseInt(id, 10)),
        participant_user_ids: formData.participant_user_ids.map(id => parseInt(id, 10)),
        comment: formData.comment?.trim() || null,
        meeting_datetime: meetingDatetime.toISOString(),
      };

      await onSubmit(data);
    } catch (err) {
      setError(err.message || 'Ошибка при сохранении встречи');
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEditing ? 'Редактировать встречу' : 'Запланировать встречу'}
      size="lg"
      radius="xl"
      styles={{
        content: {
          borderRadius: '16px',
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              {error}
            </Alert>
          )}

          {/* С кем встреча (множественный выбор) */}
          <MultiSelect
            label="С кем встреча"
            placeholder="Выберите контакты"
            required
            data={contactOptions}
            value={formData.contact_ids}
            onChange={(value) => setFormData({ ...formData, contact_ids: value })}
            searchable
            nothingFoundMessage="Контакты не найдены"
            leftSection={<IconCalendarEvent size={16} />}
          />

          {/* Кто проводит встречу (множественный выбор) */}
          <MultiSelect
            label="Кто проводит встречу"
            placeholder="Выберите участников"
            required
            data={userOptions}
            value={formData.participant_user_ids}
            onChange={(value) => setFormData({ ...formData, participant_user_ids: value })}
            searchable
            nothingFoundMessage={loadingUsers ? 'Загрузка...' : 'Участники не найдены'}
          />

          <Divider label="Дата и время" labelPosition="left" />

          {/* Дата и время */}
          <Group grow>
            <DatePickerInput
              label="Дата"
              placeholder="Выберите дату"
              required
              value={formData.meeting_date}
              onChange={(value) => setFormData({ ...formData, meeting_date: value })}
              leftSection={<IconCalendar size={16} />}
              valueFormat="DD.MM.YYYY"
              locale="ru"
            />
            <TimeInput
              label="Время"
              placeholder="00:00"
              required
              value={formData.meeting_time}
              onChange={(e) => setFormData({ ...formData, meeting_time: e.target.value })}
              leftSection={<IconClock size={16} />}
            />
          </Group>

          {/* Комментарий */}
          <Textarea
            label="Комментарий"
            placeholder="Цели встречи, заметки..."
            autosize
            minRows={4}
            value={formData.comment}
            onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
          />

          {/* Кнопки */}
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={onClose}>
              Отмена
            </Button>
            <Button type="submit" loading={loading}>
              {isEditing ? 'Сохранить' : 'Запланировать'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default MeetingFormModal;
